/**
 * Client intake fields for CRM manual entry (English UI)
 */

import React from 'react'
import { INTAKE_SERVICE_LABELS } from '../../../lib/intake-display'

export const CRM_INTAKE_SERVICE_OPTIONS = [
  'individual_counseling',
  'family_couples',
  'support_group',
  'professional_consultation',
]

const inputClass =
  'w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-green-600 focus:border-green-600'

export const emptyIntakePerson = () => ({
  first_name: '',
  last_name: '',
  dob: '',
  email: '',
  phone: '',
})

export const emptyIntakeMinor = () => ({
  guardian_name: '',
  guardian_phone: '',
  guardian_email: '',
  minor_full_name: '',
  minor_dob: '',
})

export function buildManualIntakePayload({
  clientType,
  serviceType,
  language,
  adult,
  minor,
  person1,
  person2,
}) {
  const base = { client_type: clientType, service_type: serviceType, language }
  if (clientType === 'adult') return { ...base, adult }
  if (clientType === 'minor') return { ...base, minor }
  return { ...base, person1, person2 }
}

function PersonFields({ person, setPerson, prefix = '' }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">First name</label>
        <input
          type="text"
          required
          className={inputClass}
          value={person.first_name}
          onChange={(e) => setPerson((p) => ({ ...p, first_name: e.target.value }))}
          autoComplete={prefix ? `${prefix}-given-name` : 'given-name'}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Last name</label>
        <input
          type="text"
          required
          className={inputClass}
          value={person.last_name}
          onChange={(e) => setPerson((p) => ({ ...p, last_name: e.target.value }))}
          autoComplete={prefix ? `${prefix}-family-name` : 'family-name'}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Date of birth</label>
        <input
          type="date"
          required
          className={inputClass}
          value={person.dob}
          onChange={(e) => setPerson((p) => ({ ...p, dob: e.target.value }))}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
        <input
          type="tel"
          required
          className={inputClass}
          value={person.phone}
          onChange={(e) => setPerson((p) => ({ ...p, phone: e.target.value }))}
          autoComplete={prefix ? `${prefix}-tel` : 'tel'}
        />
      </div>
      <div className="sm:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
        <input
          type="email"
          required
          className={inputClass}
          value={person.email}
          onChange={(e) => setPerson((p) => ({ ...p, email: e.target.value }))}
          autoComplete={prefix ? `${prefix}-email` : 'email'}
        />
      </div>
    </div>
  )
}

export default function CrmManualIntakeForm({
  clientType,
  setClientType,
  serviceType,
  setServiceType,
  language,
  setLanguage,
  adult,
  setAdult,
  minor,
  setMinor,
  person1,
  setPerson1,
  person2,
  setPerson2,
}) {
  return (
    <div className="space-y-6">
      <fieldset>
        <legend className="text-sm font-medium text-gray-900 mb-3">Who is this intake for?</legend>
        <div className="flex flex-wrap gap-4">
          {[
            { value: 'adult', label: 'Adult' },
            { value: 'minor', label: 'Minor' },
            { value: 'couple', label: 'Couple' },
          ].map(({ value, label }) => (
            <label key={value} className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="crmIntakeClientType"
                value={value}
                checked={clientType === value}
                onChange={() => setClientType(value)}
                className="text-green-600 focus:ring-green-600"
              />
              <span className="text-sm text-gray-800">{label}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">Service requested</label>
          <select
            required
            className={inputClass}
            value={serviceType}
            onChange={(e) => setServiceType(e.target.value)}
          >
            {CRM_INTAKE_SERVICE_OPTIONS.map((key) => (
              <option key={key} value={key}>
                {INTAKE_SERVICE_LABELS[key]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Client communication language
          </label>
          <select
            className={inputClass}
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
          >
            <option value="en">English</option>
            <option value="pt">Portuguese</option>
          </select>
        </div>
      </div>

      {clientType === 'adult' && <PersonFields person={adult} setPerson={setAdult} />}

      {clientType === 'minor' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Guardian name</label>
            <input
              type="text"
              required
              className={inputClass}
              value={minor.guardian_name}
              onChange={(e) => setMinor((m) => ({ ...m, guardian_name: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Guardian phone</label>
            <input
              type="tel"
              required
              className={inputClass}
              value={minor.guardian_phone}
              onChange={(e) => setMinor((m) => ({ ...m, guardian_phone: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Guardian email</label>
            <input
              type="email"
              required
              className={inputClass}
              value={minor.guardian_email}
              onChange={(e) => setMinor((m) => ({ ...m, guardian_email: e.target.value }))}
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Minor full name</label>
            <input
              type="text"
              required
              className={inputClass}
              value={minor.minor_full_name}
              onChange={(e) => setMinor((m) => ({ ...m, minor_full_name: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Minor date of birth</label>
            <input
              type="date"
              required
              className={inputClass}
              value={minor.minor_dob}
              onChange={(e) => setMinor((m) => ({ ...m, minor_dob: e.target.value }))}
            />
          </div>
        </div>
      )}

      {clientType === 'couple' && (
        <div className="space-y-8">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Person 1</h3>
            <PersonFields person={person1} setPerson={setPerson1} prefix="p1" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Person 2</h3>
            <PersonFields person={person2} setPerson={setPerson2} prefix="p2" />
          </div>
        </div>
      )}
    </div>
  )
}
